import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useJobPolling } from '../lib/useJobPolling.js';
import { StatusStamp } from '../components/StatusStamp.js';
import {
  IconLightbulb,
  IconCheckCircle,
  IconRefresh,
  IconTrash,
  IconArrowLeft,
  IconSparkle,
  IconCheck,
  IconXCircle,
  IconHourglass,
  IconPalette,
  IconFilmReel,
  IconSend,
  IconFileText,
  IconCopy,
  IconVeo,
} from '../components/Icons.js';

interface Concept {
  id: string;
  title: string;
  hook: string;
  angle: string;
  productId: string;
}

interface LogRow {
  id: string;
  stage: string;
  status: 'started' | 'succeeded' | 'failed';
  durationMs: number | null;
  message: string | null;
  createdAt: string;
}

interface FormattedPrompts {
  midjourney: string;
  sora: string;
  kling: string;
  runway: string;
  veo: string;
  generic: string;
}

interface DramaDialogue {
  narrator?: string | null;
  dialogue?: Array<{ character: string; line: string; action?: string }> | null;
  stageDirection?: string | null;
}

interface DramaExtras {
  screenplay: string;
  characterBrief: string;
  sceneMoodBoard: string;
}

interface SceneRow {
  id: string;
  orderIndex: number;
  status: 'pending' | 'generating' | 'done' | 'failed';
  durationSeconds: number;
  visualDescription: string;
  scenePrompt: string | null;
  voiceoverLine: string | null;
  formattedPrompts: FormattedPrompts | null;
  dramaDialogue?: DramaDialogue | null;
  dramaExtras?: DramaExtras | null;
}

const STAGE_LABEL: Record<string, string> = {
  PLANNING: 'Perencanaan',
  CONCEPT_GENERATION: 'Membuat konsep',
  STORYBOARD_DRAFT: 'Menyusun storyboard',
  HUMAN_REVIEW: 'Menunggu persetujuan',
  PROMPT_EXPORT: 'Membuat prompt AI',
  COMPLETED: 'Selesai',
};

type ToolKey = keyof FormattedPrompts;

const TOOL_LABELS: Record<ToolKey, { label: string; Icon: React.ComponentType<{ size?: number }> }> = {
  midjourney: { label: 'Midjourney', Icon: IconPalette },
  sora:       { label: 'Sora',       Icon: IconFilmReel },
  kling:      { label: 'Kling',      Icon: IconFilmReel },
  runway:     { label: 'Runway',     Icon: IconSend },
  veo:        { label: 'Google Veo', Icon: IconVeo },
  generic:    { label: 'Generic',    Icon: IconFileText },
};

function dotColor(status: LogRow['status']) {
  if (status === 'failed') return 'var(--status-error)';
  if (status === 'succeeded') return 'var(--status-success)';
  return 'var(--status-pending)';
}

function sceneStatusColor(status: SceneRow['status']) {
  if (status === 'failed') return 'var(--status-error)';
  if (status === 'done') return 'var(--status-success)';
  if (status === 'generating') return 'var(--status-pending)';
  return 'var(--text-muted)';
}

function SceneStatusIcon({ status }: { status: SceneRow['status'] }) {
  const color = sceneStatusColor(status);
  if (status === 'failed')    return <IconXCircle size={12} color={color} />;
  if (status === 'done')      return <IconCheck size={12} color={color} />;
  if (status === 'generating') return <IconHourglass size={12} color={color} />;
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', border: '1.5px solid var(--text-muted)' }} />;
}

function sceneStatusLabel(status: SceneRow['status']) {
  if (status === 'failed')    return 'Gagal';
  if (status === 'done')      return 'Selesai';
  if (status === 'generating') return 'Proses...';
  return 'Antrian';
}

/** Panel status scene saat PROMPT_EXPORT sedang berjalan */
function SceneExportStatusPanel({ scenes }: { scenes: SceneRow[] }) {
  if (scenes.length === 0) return null;
  const doneCount = scenes.filter((s) => s.status === 'done').length;
  const failedCount = scenes.filter((s) => s.status === 'failed').length;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>
          Membuat prompt ({doneCount}/{scenes.length} scene)
        </span>
        {failedCount > 0 && (
          <span style={{ fontSize: 12, color: 'var(--status-error)' }}>{failedCount} gagal</span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {scenes.slice().sort((a, b) => a.orderIndex - b.orderIndex).map((scene) => (
          <div key={scene.id} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px', borderRadius: 8,
            background: 'var(--bg-surface-raised)',
            border: `1px solid ${scene.status === 'failed' ? 'rgba(229,72,77,0.3)' : 'var(--border-subtle)'}`,
          }}>
            <SceneStatusIcon status={scene.status} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500 }}>Scene {scene.orderIndex + 1}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {scene.visualDescription}
              </div>
            </div>
            <span style={{ fontSize: 11, color: sceneStatusColor(scene.status), fontWeight: 500, flexShrink: 0 }}>
              {sceneStatusLabel(scene.status)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Tombol copy ke clipboard dengan feedback visual */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback: select text
    }
  }

  return (
    <button
      onClick={handleCopy}
      style={{
        padding: '3px 8px', fontSize: 10, borderRadius: 4, cursor: 'pointer',
        background: copied ? 'var(--status-success)' : 'var(--bg-surface-raised)',
        border: `1px solid ${copied ? 'var(--status-success)' : 'var(--border-subtle)'}`,
        color: copied ? '#fff' : 'var(--text-muted)',
        transition: 'all 0.2s', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 4,
      }}
    >
      {copied ? <IconCheck size={10} color="#fff" /> : <IconCopy size={10} />}
      {copied ? 'Disalin' : 'Salin'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Drama extra tabs
// ---------------------------------------------------------------------------

type DramaTab = 'screenplay' | 'character_brief' | 'mood_board';

const DRAMA_EXTRA_TABS: Array<{ key: DramaTab; label: string; emoji: string }> = [
  { key: 'screenplay',      label: 'Screenplay',      emoji: '📄' },
  { key: 'character_brief', label: 'Character Brief',  emoji: '🎭' },
  { key: 'mood_board',      label: 'Mood Board',       emoji: '🎨' },
];

/** Panel tambahan drama — screenplay, character brief, mood board */
function DramaExtrasPanel({ extras }: { extras: DramaExtras }) {
  const [activeTab, setActiveTab] = useState<DramaTab>('screenplay');

  const content: Record<DramaTab, string> = {
    screenplay:      extras.screenplay,
    character_brief: extras.characterBrief,
    mood_board:      extras.sceneMoodBoard,
  };

  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
        {DRAMA_EXTRA_TABS.map(({ key, label, emoji }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            style={{
              padding: '4px 10px', fontSize: 11, borderRadius: 20, cursor: 'pointer',
              background: activeTab === key ? '#6366f1' : 'var(--bg-surface-raised)',
              color: activeTab === key ? '#fff' : 'var(--text-muted)',
              border: `1px solid ${activeTab === key ? '#6366f1' : 'var(--border-subtle)'}`,
              fontWeight: activeTab === key ? 600 : 400,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            {emoji} {label}
          </button>
        ))}
      </div>
      <div style={{
        background: 'var(--bg-surface-raised)', borderRadius: 8,
        padding: '10px 12px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
          <CopyButton text={content[activeTab]} />
        </div>
        <pre style={{
          fontSize: 12, lineHeight: 1.7, margin: 0,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontFamily: activeTab === 'screenplay' ? 'monospace' : 'inherit',
          color: 'var(--text-primary)',
        }}>
          {content[activeTab]}
        </pre>
      </div>
    </div>
  );
}

/** Panel dialogue drama (narrator + dialogue campuran) */
function DramaDialoguePanel({ dialogue }: { dialogue: DramaDialogue }) {
  if (!dialogue.narrator && (!dialogue.dialogue || dialogue.dialogue.length === 0)) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
        🎙 Narasi & Dialog
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {dialogue.stageDirection && (
          <div style={{
            fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic',
            padding: '5px 8px', borderLeft: '3px solid var(--border-subtle)',
          }}>
            [{dialogue.stageDirection}]
          </div>
        )}
        {dialogue.narrator && (
          <div style={{
            fontSize: 12, color: 'var(--text-muted)', padding: '7px 10px',
            background: 'var(--bg-surface-raised)', borderRadius: 6,
            borderLeft: '3px solid var(--border-subtle)',
          }}>
            <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
              NARATOR
            </span>
            {dialogue.narrator}
          </div>
        )}
        {dialogue.dialogue?.map((line, i) => (
          <div key={i} style={{
            fontSize: 12, padding: '7px 10px',
            background: 'var(--bg-surface-raised)', borderRadius: 6,
            borderLeft: '3px solid var(--gold-300)',
          }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold-300)', display: 'block', marginBottom: 2 }}>
              {line.character.toUpperCase()}
            </span>
            {line.action && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>({line.action}) </span>
            )}
            {line.line}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Panel hasil PROMPT_EXPORT untuk satu scene — expandable, drama-aware */
function ScenePromptsCard({ scene, index }: { scene: SceneRow; index: number }) {
  const [open, setOpen] = useState(index === 0);
  const [activeTool, setActiveTool] = useState<ToolKey>('midjourney');

  if (!scene.formattedPrompts) return null;
  const prompts = scene.formattedPrompts;
  const isDrama = !!(scene.dramaExtras || scene.dramaDialogue);

  return (
    <div style={{
      borderRadius: 10, overflow: 'hidden',
      border: `1px solid ${isDrama ? 'rgba(99,102,241,0.3)' : 'var(--border-subtle)'}`,
      background: 'var(--bg-surface)',
      marginBottom: 8,
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 12px', background: 'transparent', cursor: 'pointer',
          borderBottom: open ? '1px solid var(--border-subtle)' : 'none',
        }}
      >
        <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            {isDrama && <span style={{ fontSize: 10 }}>🎬</span>}
            Scene {scene.orderIndex + 1}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {scene.durationSeconds}s · {scene.visualDescription}
          </div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{ padding: '10px 12px' }}>
          {/* Tab selector untuk tools */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
            {(Object.keys(TOOL_LABELS) as ToolKey[]).map((tool) => {
              const { label, Icon } = TOOL_LABELS[tool];
              return (
                <button
                  key={tool}
                  onClick={() => setActiveTool(tool)}
                  style={{
                    padding: '4px 10px', fontSize: 11, borderRadius: 20, cursor: 'pointer',
                    background: activeTool === tool ? 'var(--gold-300)' : 'var(--bg-surface-raised)',
                    color: activeTool === tool ? '#000' : 'var(--text-muted)',
                    border: `1px solid ${activeTool === tool ? 'var(--gold-300)' : 'var(--border-subtle)'}`,
                    fontWeight: activeTool === tool ? 600 : 400,
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}
                >
                  <Icon size={12} /> {label}
                </button>
              );
            })}
          </div>

          {/* Prompt text + tombol salin */}
          <div style={{
            background: 'var(--bg-surface-raised)', borderRadius: 8,
            padding: '10px 12px', position: 'relative',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <p style={{
                fontSize: 12, lineHeight: 1.6, margin: 0,
                fontFamily: 'monospace', wordBreak: 'break-word', flex: 1,
                color: 'var(--text-primary)',
              }}>
                {prompts[activeTool]}
              </p>
              <CopyButton text={prompts[activeTool]} />
            </div>
          </div>

          {/* Drama: narasi + dialogue */}
          {isDrama && scene.dramaDialogue && (
            <DramaDialoguePanel dialogue={scene.dramaDialogue} />
          )}

          {/* Narasi (pipeline iklan) */}
          {!isDrama && scene.voiceoverLine && (
            <div style={{ marginTop: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Narasi
                </div>
                <CopyButton text={scene.voiceoverLine} />
              </div>
              <div style={{
                fontSize: 12, color: 'var(--text-muted)', padding: '6px 10px',
                background: 'var(--bg-surface-raised)', borderRadius: 6,
                borderLeft: '3px solid var(--border-subtle)',
              }}>
                {scene.voiceoverLine}
              </div>
            </div>
          )}

          {/* Drama extras: screenplay + character brief + mood board */}
          {scene.dramaExtras && (
            <DramaExtrasPanel extras={scene.dramaExtras} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Bangun teks untuk satu tool (semua scene).
 */
function buildToolText(scenes: SceneRow[], tool: ToolKey): string {
  return scenes
    .filter((s) => s.formattedPrompts)
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((s, i) => {
      const header = `Scene ${i + 1} (${s.durationSeconds}s):`;
      const prompt = s.formattedPrompts![tool];
      const narasi = s.voiceoverLine ? `\nNarasi: ${s.voiceoverLine}` : '';
      return `${header}\n${prompt}${narasi}`;
    })
    .join('\n\n');
}

/**
 * Download semua tool sebagai bundle teks — satu file .txt dengan section per tool.
 */
function downloadAllTools(scenes: SceneRow[]) {
  const sections = (Object.keys(TOOL_LABELS) as ToolKey[]).map((tool) => {
    const { label } = TOOL_LABELS[tool];
    const divider = '='.repeat(40);
    return `${divider}\n${label.toUpperCase()}\n${divider}\n\n${buildToolText(scenes, tool)}`;
  });
  const blob = new Blob([sections.join('\n\n\n')], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `prompts-all-tools.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Salin semua prompts (semua scene, satu tool) sebagai teks terformat */
function CopyAllButton({ scenes, tool }: { scenes: SceneRow[]; tool: ToolKey }) {
  const [copied, setCopied] = useState(false);

  async function handleCopyAll() {
    const text = buildToolText(scenes, tool);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }

  const { label, Icon } = TOOL_LABELS[tool];

  return (
    <button
      onClick={handleCopyAll}
      style={{
        padding: '6px 14px', fontSize: 12, borderRadius: 6, cursor: 'pointer',
        background: copied ? 'var(--status-success)' : 'var(--bg-surface-raised)',
        border: `1px solid ${copied ? 'var(--status-success)' : 'var(--border-subtle)'}`,
        color: copied ? '#fff' : 'var(--text-primary)',
        fontWeight: 500,
        display: 'inline-flex', alignItems: 'center', gap: 6,
      }}
    >
      {copied ? <IconCheck size={13} color="#fff" /> : <Icon size={13} />}
      {copied ? 'Disalin!' : `Salin Semua (${label})`}
    </button>
  );
}

/** Panel hasil lengkap PROMPT_EXPORT saat job completed */
function PromptsResultPanel({ scenes }: { scenes: SceneRow[] }) {
  const [globalTool, setGlobalTool] = useState<ToolKey>('midjourney');
  const donedScenes = scenes.filter((s) => s.formattedPrompts).sort((a, b) => a.orderIndex - b.orderIndex);

  if (donedScenes.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <IconSparkle size={16} /> Prompts ({donedScenes.length} scene)
        </span>
        <CopyAllButton scenes={donedScenes} tool={globalTool} />
      </div>

      {/* Tool selector global untuk "salin semua" */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 4 }}>Salin untuk:</span>
        {(Object.keys(TOOL_LABELS) as ToolKey[]).map((tool) => {
          const { label, Icon } = TOOL_LABELS[tool];
          return (
            <button
              key={tool}
              onClick={() => setGlobalTool(tool)}
              style={{
                padding: '3px 8px', fontSize: 11, borderRadius: 20, cursor: 'pointer',
                background: globalTool === tool ? 'var(--gold-300)' : 'transparent',
                color: globalTool === tool ? '#000' : 'var(--text-muted)',
                border: `1px solid ${globalTool === tool ? 'var(--gold-300)' : 'var(--border-subtle)'}`,
                fontWeight: globalTool === tool ? 600 : 400,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            >
              <Icon size={11} /> {label}
            </button>
          );
        })}
      </div>

      {/* Export semua tool sekaligus sebagai .txt */}
      <div style={{ marginBottom: 12 }}>
        <button
          onClick={() => downloadAllTools(donedScenes)}
          style={{
            padding: '5px 12px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
            background: 'var(--bg-surface-raised)',
            border: '1px solid var(--border-subtle)',
            color: 'var(--text-muted)',
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}
          title="Download semua prompt (semua tool) sebagai file .txt"
        >
          ↓ Export Semua Tool (.txt)
        </button>
      </div>

      {/* Card per scene */}
      {donedScenes.map((scene, i) => (
        <ScenePromptsCard key={scene.id} scene={scene} index={i} />
      ))}
    </div>
  );
}

// ─── Social Captions ────────────────────────────────────────────────────────

interface SocialCaptions {
  instagram: string;
  tiktok: string;
  twitter: string;
  facebook: string;
}

type SocialPlatform = keyof SocialCaptions;

const SOCIAL_LABELS: Record<SocialPlatform, { label: string; emoji: string; hint: string }> = {
  instagram: { label: 'Instagram', emoji: '📸', hint: 'Caption + hashtag panjang' },
  tiktok:    { label: 'TikTok',    emoji: '🎵', hint: 'Hook pendek + hashtag' },
  twitter:   { label: 'Twitter/X', emoji: '𝕏', hint: 'Max 240 karakter' },
  facebook:  { label: 'Facebook',  emoji: '👥', hint: 'Caption informatif' },
};

/** Panel caption sosmed siap copy untuk keseluruhan video */
function SocialCaptionsPanel({ captions }: { captions: SocialCaptions }) {
  const [active, setActive] = useState<SocialPlatform>('instagram');
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(captions[active]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }

  return (
    <div style={{ marginTop: 16 }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10,
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          📣 Caption Sosmed
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {SOCIAL_LABELS[active].hint}
        </span>
      </div>

      {/* Platform tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
        {(Object.keys(SOCIAL_LABELS) as SocialPlatform[]).map((p) => {
          const { label, emoji } = SOCIAL_LABELS[p];
          const isActive = p === active;
          return (
            <button
              key={p}
              onClick={() => { setActive(p); setCopied(false); }}
              style={{
                padding: '4px 10px', fontSize: 11, borderRadius: 20, cursor: 'pointer',
                background: isActive ? 'var(--gold-300)' : 'var(--bg-surface-raised)',
                color: isActive ? '#000' : 'var(--text-muted)',
                border: `1px solid ${isActive ? 'var(--gold-300)' : 'var(--border-subtle)'}`,
                fontWeight: isActive ? 600 : 400,
                display: 'inline-flex', alignItems: 'center', gap: 5,
              }}
            >
              {emoji} {label}
            </button>
          );
        })}
      </div>

      {/* Caption text + copy */}
      <div style={{
        background: 'var(--bg-surface-raised)', borderRadius: 8,
        padding: '10px 12px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {SOCIAL_LABELS[active].emoji} {SOCIAL_LABELS[active].label}
          </span>
          <button
            onClick={handleCopy}
            style={{
              padding: '3px 8px', fontSize: 10, borderRadius: 4, cursor: 'pointer',
              background: copied ? 'var(--status-success)' : 'var(--bg-surface-raised)',
              border: `1px solid ${copied ? 'var(--status-success)' : 'var(--border-subtle)'}`,
              color: copied ? '#fff' : 'var(--text-muted)',
              transition: 'all 0.2s', flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            {copied ? <IconCheck size={10} color="#fff" /> : <IconCopy size={10} />}
            {copied ? 'Disalin!' : 'Salin'}
          </button>
        </div>
        <p style={{
          fontSize: 12, lineHeight: 1.7, margin: 0,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          color: 'var(--text-primary)',
        }}>
          {captions[active]}
        </p>
      </div>
    </div>
  );
}

// ─── ConceptPickerPanel ──────────────────────────────────────────────────────

/**
 * ConceptPickerPanel — pilih 1 konsep dari yang digenerate LLM.
 * Tombol "Buat Konsep Baru" memanggil POST /api/bff/concepts dengan jobId
 * sehingga mockResult.conceptIds di-update dan panel reload otomatis.
 */
function ConceptPickerPanel({ jobId, productId, onSelected }: { jobId: string; productId: string; onSelected: () => void }) {
  const [concepts, setConcepts]       = useState<Concept[]>([]);
  const [loading, setLoading]         = useState(true);
  const [selecting, setSelecting]     = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError]             = useState<string | null>(null);

  async function loadConcepts() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ data: Concept[] }>(`/api/bff/concepts?productId=${productId}&jobId=${jobId}`);
      setConcepts(res.data);
    } catch {
      setError('Gagal memuat konsep');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadConcepts(); }, [productId, jobId]);

  async function handleSelect(conceptId: string) {
    setSelecting(conceptId);
    setError(null);
    try {
      await api.post(`/api/bff/concepts/${conceptId}/select`);
      onSelected(); // trigger re-poll segera di parent
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal memilih konsep');
    } finally {
      setSelecting(null);
    }
  }

  /**
   * Minta LLM generate 1 konsep baru. Backend menyimpannya ke DB dan
   * mengupdate job.mockResult.conceptIds sehingga loadConcepts() berikutnya
   * hanya menampilkan konsep baru (menggantikan yang lama).
   */
  async function handleRegenerate() {
    setRegenerating(true);
    setError(null);
    try {
      await api.post('/api/bff/concepts', { productId, jobId, count: 1 });
      await loadConcepts(); // reload — tampil konsep baru dari mockResult.conceptIds terbaru
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal membuat konsep baru');
    } finally {
      setRegenerating(false);
    }
  }

  const isBusy = !!selecting || regenerating;

  if (loading) {
    return (
      <div className="card" style={{ marginTop: 12, textAlign: 'center', padding: '24px 16px', color: 'var(--text-muted)' }}>
        Memuat konsep...
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      {/* Header + tombol regenerate */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconLightbulb size={15} /> Pilih Konsep Video
        </div>
        <button
          className="btn-ghost"
          onClick={handleRegenerate}
          disabled={isBusy}
          title="Generate ulang konsep baru dari LLM"
          style={{
            padding: '5px 10px',
            fontSize: 11,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            opacity: isBusy ? 0.5 : 1,
          }}
        >
          <IconRefresh size={12} />
          {regenerating ? 'Membuat...' : 'Konsep Baru'}
        </button>
      </div>

      {/* Error state */}
      {error && (
        <p style={{ color: 'var(--status-error)', fontSize: 12, marginBottom: 8 }}>{error}</p>
      )}

      {/* Skeleton saat regenerate (konsep lama diganti) */}
      {regenerating && (
        <div style={{
          padding: '20px 14px', borderRadius: 10, textAlign: 'center',
          background: 'var(--bg-surface-raised)', border: '1px dashed var(--border-subtle)',
          color: 'var(--text-muted)', fontSize: 12,
        }}>
          <IconSparkle size={16} style={{ marginBottom: 6 }} />
          <div>LLM sedang membuat konsep baru...</div>
        </div>
      )}

      {/* Daftar konsep */}
      {!regenerating && concepts.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '20px 14px' }}>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Belum ada konsep — menunggu generate selesai atau buat konsep baru.
          </p>
          <button className="btn-ghost" onClick={handleRegenerate} disabled={isBusy} style={{ fontSize: 12 }}>
            <IconRefresh size={13} /> Coba Generate Konsep
          </button>
        </div>
      )}

      {!regenerating && concepts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {concepts.map((c) => (
            <div key={c.id} style={{
              padding: '12px 14px', borderRadius: 10,
              background: 'var(--bg-surface-raised)', border: '1px solid var(--border-subtle)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{c.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                <strong>Hook:</strong> {c.hook}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>{c.angle}</div>
              <button
                className="btn-primary"
                style={{ width: '100%', padding: '8px' }}
                onClick={() => handleSelect(c.id)}
                disabled={isBusy}
              >
                {selecting === c.id ? 'Memilih...' : 'Pilih Konsep Ini →'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { job, refresh: refreshJob } = useJobPolling(id ?? null);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [scenes, setScenes] = useState<SceneRow[]>([]);
  const [productName, setProductName] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryingFailedScenes, setRetryingFailedScenes] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [startingNew, setStartingNew] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<{ data: LogRow[] }>(`/api/bff/jobs/${id}/logs`);
      setLogs(res.data);
    } catch { /* ignore */ }
  }, [id]);

  const fetchScenes = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<{ data: SceneRow[] }>(`/api/bff/jobs/${id}/scenes`);
      setScenes(res.data);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    fetchLogs();
    fetchScenes();
  }, [fetchLogs, fetchScenes]);

  useEffect(() => {
    if (job?.currentStep) {
      fetchLogs();
      fetchScenes();
    }
  }, [job?.currentStep, fetchLogs, fetchScenes]);

  // Fetch nama produk sekali — hanya untuk label header
  useEffect(() => {
    if (!job?.productId || productName) return;
    api.get<{ data: { id: string; name: string } }>(`/api/bff/products/${job.productId}`)
      .then((res) => setProductName(res.data.name))
      .catch(() => { /* tidak kritis */ });
  }, [job?.productId, productName]);

  async function handleStartNewJob() {
    if (!job?.productId) return;
    setStartingNew(true);
    try {
      const res = await api.post<{ data: { id: string } }>('/api/bff/generate', {
        productId: job.productId,
      });
      navigate(`/jobs/${res.data.id}`);
    } catch {
      // gagal diam-diam — user bisa ke Studio
    } finally {
      setStartingNew(false);
    }
  }

  async function handleRetry() {
    if (!id) return;
    setRetrying(true);
    setRetryError(null);
    try {
      await api.post(`/api/bff/jobs/${id}/retry`);
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Gagal retry job');
    } finally {
      setRetrying(false);
    }
  }

  async function handleRetryFailedScenes() {
    if (!id) return;
    setRetryingFailedScenes(true);
    setRetryError(null);
    try {
      await api.post(`/api/bff/jobs/${id}/retry-failed-scenes`);
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Gagal retry scene yang gagal');
    } finally {
      setRetryingFailedScenes(false);
    }
  }

  async function handleDelete() {
    if (!id || !window.confirm('Hapus job ini? Tindakan tidak bisa dibatalkan.')) return;
    setDeleting(true);
    try {
      await api.delete(`/api/bff/jobs/${id}`);
      navigate('/jobs');
    } catch { /* ignore */ } finally {
      setDeleting(false);
    }
  }

  if (!job) return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Memuat...</div>;

  const isPromptFailure =
    job.currentStep === 'failed' &&
    job.errorCode === 'stage:PROMPT_EXPORT';

  const failedSceneCount = scenes.filter((s) => s.status === 'failed').length;
  const doneSceneCount = scenes.filter((s) => s.status === 'done').length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn-ghost tap-target icon-btn" onClick={() => navigate('/jobs')}>
          <IconArrowLeft size={16} /> Kembali
        </button>
        {(job.currentStep === 'failed' || job.currentStep === 'completed') && (
          <button
            className="btn-ghost tap-target icon-btn"
            onClick={handleDelete}
            disabled={deleting}
            style={{ color: 'var(--status-error)', borderColor: 'var(--status-error)' }}
          >
            <IconTrash size={16} /> Hapus
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        {/* Header: nama produk + job ID */}
        <div style={{ marginBottom: 4 }}>
          {productName && (
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{productName}</div>
          )}
          <div className="text-muted mono" style={{ fontSize: 12 }}>#{job.id.slice(-8)}</div>
        </div>
        <div style={{ margin: '6px 0' }}>
          <StatusStamp status={job.currentStep} />
        </div>
        <div className="progress-track">
          <div
            className={`progress-fill${job.currentStep === 'failed' ? ' progress-fill--error' : job.currentStep === 'completed' ? ' progress-fill--success' : ''}`}
            style={{ width: `${job.progress}%` }}
          />
        </div>

        {/* Error state */}
        {job.currentStep === 'failed' && (
          <>
            <p style={{ color: 'var(--status-error)', fontSize: 13, marginTop: 10 }}>{job.errorMessage}</p>
            {retryError && <p style={{ color: 'var(--status-error)', fontSize: 12, marginTop: 6 }}>{retryError}</p>}

            {/* Partial failure */}
            {isPromptFailure && doneSceneCount > 0 && failedSceneCount > 0 && (
              <>
                <button className="btn-primary" style={{ width: '100%', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  onClick={handleRetryFailedScenes} disabled={retryingFailedScenes || retrying}>
                  <IconRefresh size={16} />
                  {retryingFailedScenes ? 'Mencoba ulang...' : `Ulangi ${failedSceneCount} Scene Gagal`}
                </button>
                <button className="btn-ghost" style={{ width: '100%', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  onClick={handleRetry} disabled={retryingFailedScenes || retrying}>
                  <IconRefresh size={16} />
                  {retrying ? 'Mencoba ulang...' : 'Ulangi dari Awal Stage Ini'}
                </button>
              </>
            )}

            {/* Full failure */}
            {(!isPromptFailure || (isPromptFailure && doneSceneCount === 0)) && (
              <button className="btn-primary" style={{ width: '100%', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                onClick={handleRetry} disabled={retrying}>
                <IconRefresh size={16} />
                {retrying ? 'Mencoba ulang...' : 'Coba Lagi'}
              </button>
            )}
          </>
        )}

        {/* PROMPT_EXPORT sedang berjalan */}
        {job.currentStep === 'exporting_prompts' && scenes.length > 0 && (
          <SceneExportStatusPanel scenes={scenes} />
        )}

        {/* Pilih konsep */}
        {(job.currentStep === 'concepts_ready' ||
          (job.currentStep === 'awaiting_approval' && !job.storyboardId)) && (
          <ConceptPickerPanel jobId={job.id} productId={job.productId} onSelected={refreshJob} />
        )}

        {/* Review storyboard */}
        {job.currentStep === 'awaiting_approval' && job.storyboardId && (
          <button
            className="btn-primary"
            style={{ width: '100%', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={() => navigate(`/storyboard/${job.storyboardId}`)}>
            <IconCheckCircle size={16} /> Review Storyboard
          </button>
        )}

        {/* Hasil prompts + caption sosmed saat selesai */}
        {job.currentStep === 'completed' && scenes.length > 0 && (
          <>
            <PromptsResultPanel scenes={scenes} />
            {job.mockResult?.socialCaptions && (
              <SocialCaptionsPanel
                captions={job.mockResult.socialCaptions as SocialCaptions}
              />
            )}
            {/* Shortcut: mulai job baru untuk produk yang sama */}
            <div style={{
              marginTop: 16, paddingTop: 14,
              borderTop: '1px solid var(--border-subtle)',
              display: 'flex', flexDirection: 'column', gap: 8,
            }}>
              <p className="text-muted" style={{ fontSize: 12, textAlign: 'center' }}>
                Ingin versi lain?
              </p>
              <button
                className="btn-primary"
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                onClick={handleStartNewJob}
                disabled={startingNew}
              >
                <IconSparkle size={16} />
                {startingNew ? 'Memulai...' : 'Mulai Job Baru untuk Produk Ini'}
              </button>
            </div>
          </>
        )}

        {/* Prompts partial saat failed setelah beberapa scene berhasil */}
        {job.currentStep === 'failed' && doneSceneCount > 0 && scenes.some(s => s.formattedPrompts) && (
          <div style={{ marginTop: 8 }}>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              {doneSceneCount} scene berhasil sebelum gagal — prompts tersedia di bawah:
            </p>
            <PromptsResultPanel scenes={scenes} />
          </div>
        )}
      </div>

      {/* Timeline */}
      <h2 style={{ fontSize: 15, marginBottom: 8 }}>Timeline</h2>
      {logs.length === 0 ? (
        <p className="text-muted" style={{ fontSize: 13 }}>Belum ada log tercatat.</p>
      ) : (
        <div className="timeline">
          {logs.map((log) => (
            <div className="timeline__item" key={log.id}>
              <div className="timeline__dot" style={{ background: dotColor(log.status) }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong style={{ fontSize: 13 }}>{STAGE_LABEL[log.stage] ?? log.stage}</strong>
                  {log.durationMs != null && (
                    <span className="text-muted mono" style={{ fontSize: 11 }}>
                      {(log.durationMs / 1000).toFixed(1)}s
                    </span>
                  )}
                </div>
                {log.message && (
                  <p className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>{log.message}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
